<template>
  <AuthShell>
    <div v-if="linkInvalid" class="auth-form">
      <h1 class="auth-form__title">This invite has expired</h1>
      <p class="auth-form__sub">The link is invalid or has already been used. Ask your admin to resend it.</p>
      <v-btn color="primary" block size="large" @click="redirectToLogin">Go to sign in</v-btn>
    </div>

    <div v-else-if="accountCreated" class="auth-form">
      <h1 class="auth-form__title">Account created</h1>
      <p class="auth-form__sub">You're all set. Sign in to get started.</p>
      <v-btn color="primary" block size="large" @click="redirectToLogin">Continue to sign in</v-btn>
    </div>

    <v-form v-else class="auth-form" @submit.prevent="submit">
      <h1 class="auth-form__title">Create your account</h1>
      <p class="auth-form__sub">
        You were invited as <b>{{ email }}</b
        >. Choose a password to finish.
      </p>

      <div v-if="isProvider" class="auth-form__field">
        <label for="invite-npi" class="auth-form__label">NPI</label>
        <v-text-field id="invite-npi" v-model="npi" disabled />
      </div>

      <div class="auth-form__field">
        <label for="invite-first" class="auth-form__label">First name</label>
        <v-text-field id="invite-first" v-model="firstName" :error-messages="firstNameError" />
      </div>

      <div class="auth-form__field">
        <label for="invite-last" class="auth-form__label">Last name</label>
        <v-text-field id="invite-last" v-model="lastName" :error-messages="lastNameError" />
      </div>

      <div v-if="isProvider" class="auth-form__field">
        <span class="auth-form__label">How should pharmacies reach you?</span>
        <v-btn-toggle v-model="preference" mandatory density="compact" class="mb-3">
          <v-btn value="fax" size="small" prepend-icon="mdi-fax">Fax</v-btn>
          <v-btn value="email" size="small" prepend-icon="mdi-email-outline">Email</v-btn>
        </v-btn-toggle>
        <v-text-field
          v-model="fax"
          label="Your fax number"
          hint="Prefilled from the NPI registry — correct it if needed."
          persistent-hint />
      </div>

      <div class="auth-form__field">
        <label for="invite-password" class="auth-form__label">Password</label>
        <v-text-field
          id="invite-password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="new-password"
          :error-messages="passwordError"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPassword = !showPassword" />
      </div>
      <p class="auth-form__hint">At least 11 characters with an upper and lower case letter, a number and a symbol.</p>

      <div class="auth-form__field">
        <label for="invite-confirm" class="auth-form__label">Confirm password</label>
        <v-text-field
          id="invite-confirm"
          v-model="passwordConfirmation"
          :type="showPasswordConfirmation ? 'text' : 'password'"
          autocomplete="new-password"
          :error-messages="passwordMatchError"
          :append-inner-icon="showPasswordConfirmation ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPasswordConfirmation = !showPasswordConfirmation" />
      </div>

      <v-btn color="primary" block size="large" type="submit" :loading="submitting">Create account</v-btn>

      <v-alert v-if="errors.length > 0" type="error" variant="tonal">
        <div v-for="error in errors" :key="error">{{ error }}</div>
      </v-alert>
    </v-form>
  </AuthShell>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import authService from "@/services/authService";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";
import AuthShell from "@/components/AuthShell.vue";

const route = useRoute();
const router = useRouter();

const email = ref("");
const isProvider = ref(false);
const npi = ref("");
const preference = ref(null);
const fax = ref("");
const firstName = ref("");
const lastName = ref("");
const password = ref("");
const passwordConfirmation = ref("");
const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

const submitting = ref(false);
const submitClicked = ref(false);
const accountCreated = ref(false);
const linkInvalid = ref(false);
const errors = ref([]);

const isPasswordValid = computed(() => FORM_VALIDATION_PATTERNS.PASSWORD_PATTERN.test(password.value));
const isPasswordMatchValid = computed(() => password.value === passwordConfirmation.value);

const firstNameError = computed(() =>
  submitClicked.value && !firstName.value.trim() ? "Please enter your first name" : ""
);
const lastNameError = computed(() =>
  submitClicked.value && !lastName.value.trim() ? "Please enter your last name" : ""
);

const passwordError = computed(() => {
  if (!submitClicked.value) return "";
  if (!password.value) return "Please enter a password";
  if (!isPasswordValid.value) {
    return "Please enter a valid password (at least 11 characters, at least 1 uppercase and 1 lowercase letter, 1 number, and 1 symbol)";
  }
  return "";
});

const passwordMatchError = computed(() => {
  if (!submitClicked.value) return "";
  if (!passwordConfirmation.value) return "Please confirm your password";
  if (!isPasswordMatchValid.value) return "Passwords must match";
  return "";
});

onMounted(async () => {
  const { uidb64, token } = route.params;
  if (!uidb64 || !token) {
    linkInvalid.value = true;
    return;
  }
  try {
    const info = await authService.getInviteInfo(uidb64, token);
    email.value = info.email;
    isProvider.value = Boolean(info.isProvider);
    npi.value = info.npi || "";
    fax.value = info.fax || "";
    // Provider invites arrive with NPI-registry names; both stay editable.
    firstName.value = info.firstName || "";
    lastName.value = info.lastName || "";
  } catch {
    linkInvalid.value = true;
  }
});

const submit = async () => {
  submitClicked.value = true;
  if (firstNameError.value || lastNameError.value || passwordError.value || passwordMatchError.value) return;

  submitting.value = true;
  try {
    await authService.acceptInvite(route.params.uidb64, route.params.token, {
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
      password: password.value,
      ...(isProvider.value && preference.value ? { communication_preference: preference.value } : {}),
      ...(isProvider.value ? { fax: fax.value.trim() } : {}),
    });
    accountCreated.value = true;
    errors.value = [];
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    submitting.value = false;
  }
};

const redirectToLogin = () => router.push({ name: "login" });
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_auth-form.scss";
</style>
